import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ArrowLeft, ShoppingCart, Loader2, Minus, Plus, Package, Truck, Info, Star } from 'lucide-react';
import { apiRequest, formatPrice } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ProductReviews, { StarRating } from '../components/ProductReviews';
import RelatedProducts from '../components/RelatedProducts';

export default function ProductDetailPage() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { addToCart } = useCart();
    
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [addingToCart, setAddingToCart] = useState(false);

    useEffect(() => {
        fetchProduct();
    }, [productId]);

    const fetchProduct = async () => {
        setLoading(true);
        try {
            const data = await apiRequest(`/products/${productId}`);
            setProduct(data);
        } catch (error) {
            toast.error('Produit non trouvé');
            navigate('/shop');
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = async () => {
        if (!user) {
            toast.error('Connectez-vous pour ajouter au panier');
            navigate('/auth');
            return;
        }
        setAddingToCart(true);
        try {
            await addToCart(product.product_id, quantity);
            toast.success(`${quantity} x ${product.name} ajouté au panier`);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setAddingToCart(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen pt-20 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!product) return null;

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="product-detail-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back button */}
                <Button
                    variant="ghost"
                    className="mb-6 rounded-full"
                    onClick={() => navigate('/shop')}
                    data-testid="back-btn"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Retour à la boutique
                </Button>

                <div className="grid lg:grid-cols-2 gap-12">
                    {/* Product Image */}
                    <div className="glass rounded-3xl overflow-hidden aspect-square">
                        <img
                            src={product.image_url}
                            alt={`${product.name} - Produit piscine ${product.brand} | O.C.A Piscine`}
                            className="w-full h-full object-cover"
                        />
                    </div>

                    {/* Product Info */}
                    <div className="space-y-6">
                        <div>
                            <Badge className="mb-3">{product.brand}</Badge>
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2" data-testid="product-name">
                                {product.name}
                            </h1>
                            <p className="text-muted-foreground text-lg">
                                {product.description}
                            </p>
                        </div>

                        {/* Price */}
                        <div className="flex items-baseline gap-2">
                            <span className="font-heading text-4xl font-bold text-primary" data-testid="product-price">
                                {formatPrice(product.price)}
                            </span>
                            <span className={`text-sm ${product.stock > 10 ? 'text-emerald-500' : product.stock > 0 ? 'text-amber-500' : 'text-red-500'}`}>
                                {product.stock > 10 ? 'En stock' : product.stock > 0 ? `Plus que ${product.stock} en stock` : 'Rupture de stock'}
                            </span>
                        </div>

                        {/* Dosage info */}
                        {product.dosage_info && (
                            <Card className="bg-primary/5 border-primary/20">
                                <CardContent className="p-4 flex items-start gap-3">
                                    <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium mb-1">Dosage recommandé</p>
                                        <p className="text-sm text-muted-foreground font-mono">{product.dosage_info}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Quantity selector */}
                        <div className="flex items-center gap-4">
                            <span className="font-medium">Quantité:</span>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="secondary"
                                    size="icon"
                                    className="rounded-full h-10 w-10"
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    disabled={quantity <= 1}
                                    data-testid="qty-minus"
                                >
                                    <Minus className="h-4 w-4" />
                                </Button>
                                <Input
                                    type="number"
                                    value={quantity}
                                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="w-20 h-10 text-center rounded-full"
                                    min="1"
                                    data-testid="qty-input"
                                />
                                <Button
                                    variant="secondary"
                                    size="icon"
                                    className="rounded-full h-10 w-10"
                                    onClick={() => setQuantity(quantity + 1)}
                                    disabled={quantity >= product.stock}
                                    data-testid="qty-plus"
                                >
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>

                        {/* Add to cart button */}
                        <Button
                            size="lg"
                            className="w-full rounded-full h-14 text-lg font-semibold"
                            onClick={handleAddToCart}
                            disabled={addingToCart || product.stock === 0}
                            data-testid="add-to-cart-btn"
                        >
                            {addingToCart ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                                <>
                                    <ShoppingCart className="h-5 w-5 mr-2" />
                                    Ajouter au panier - {formatPrice(product.price * quantity)}
                                </>
                            )}
                        </Button>

                        {/* Delivery info */}
                        <div className="grid grid-cols-2 gap-4">
                            <Card className="glass">
                                <CardContent className="p-4 flex items-center gap-3">
                                    <Truck className="h-5 w-5 text-primary" />
                                    <div>
                                        <p className="font-medium text-sm">Livraison</p>
                                        <p className="text-xs text-muted-foreground">9,90€ - 2-4 jours</p>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="glass">
                                <CardContent className="p-4 flex items-center gap-3">
                                    <Package className="h-5 w-5 text-primary" />
                                    <div>
                                        <p className="font-medium text-sm">Retrait magasin</p>
                                        <p className="text-xs text-muted-foreground">Gratuit - 24h</p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Average Rating Preview */}
                        {product.average_rating > 0 && (
                            <div className="flex items-center gap-2 pt-4 border-t">
                                <StarRating rating={Math.round(product.average_rating)} readonly size="sm" />
                                <span className="font-medium">{product.average_rating}</span>
                                <span className="text-muted-foreground">
                                    ({product.total_reviews || 0} avis)
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Related Products */}
                <div className="mt-12">
                    <RelatedProducts productId={productId} />
                </div>

                {/* Product Reviews */}
                <div className="mt-12">
                    <h2 className="font-heading text-2xl font-bold mb-6">Avis clients</h2>
                    <ProductReviews productId={productId} />
                </div>
            </div>
        </div>
    );
}
